import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { teacherModuleAPI } from '../../services/api';
import { MonthlyReportBatchTeaching } from '../../components/Teacher/MonthlyReportSessionDetails';
import { MonthlyReportSnapshotFreshnessAlert } from '../../components/Teacher/MonthlyReportSnapshotFreshnessAlert';

const MIN_REJECT_COMMENT_LENGTH = 10;
const MAX_REJECT_COMMENT_LENGTH = 4000;

function rejectCommentHelperText(length) {
  if (length < MIN_REJECT_COMMENT_LENGTH) {
    return `${length}/${MIN_REJECT_COMMENT_LENGTH} characters (minimum ${MIN_REJECT_COMMENT_LENGTH})`;
  }
  return `${length} characters`;
}

function shiftMonth(month, delta) {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function teacherOptionLabel(t) {
  const name = `${t.first_name || ''} ${t.last_name || ''}`.trim();
  return name || t.email || String(t.id);
}

function StudentAssessmentStatusCell({ summary }) {
  if (!summary || summary.eligibleStudents === 0) {
    return <Typography variant="body2" color="text.secondary">No eligible students</Typography>;
  }
  if (summary.allSubmitted && summary.allApproved) {
    return <Chip size="small" color="success" label={`All ${summary.eligibleStudents} approved`} />;
  }
  if (summary.allSubmitted) {
    return (
      <Chip
        size="small"
        color="info"
        label={`${summary.submitted}/${summary.eligibleStudents} submitted`}
      />
    );
  }
  return (
    <Chip
      size="small"
      color="warning"
      label={`${summary.submitted}/${summary.eligibleStudents} submitted`}
    />
  );
}

function TeacherAchievementReportBody({ report }) {
  if (!report) return null;
  const narrativeEntries = Object.entries(report.reportContent?.narrative || {}).filter(([, v]) =>
    String(v || '').trim()
  );
  const summary = report.generatedSnapshot?.summary || {};
  const warnings = (report.warnings?.items || []).filter((w) => w.code !== 'snapshot_stale');
  return (
    <Stack spacing={2}>
      <MonthlyReportSnapshotFreshnessAlert
        generatedAt={report.generatedAt}
        snapshotStale={Boolean(report.warnings?.snapshotStale)}
        status={report.status}
        variant="supervisor"
      />
      {(summary.sessionsScheduled != null || summary.sessionsDelivered != null) && (
        <Typography variant="body2" color="text.secondary">
          Classes delivered: {summary.sessionsDelivered ?? 0} · scheduled:{' '}
          {summary.sessionsScheduled ?? 0} · students taught: {summary.uniqueStudents ?? 0} ·
          modules: {summary.moduleTypesCovered ?? 0}
        </Typography>
      )}
      {report.warnings?.studentAssessments ? (
        <Alert
          severity={report.warnings.studentAssessments.allSubmitted ? 'success' : 'warning'}
        >
          Student assessments: {report.warnings.studentAssessments.submitted}/
          {report.warnings.studentAssessments.eligibleStudents} submitted
          {report.warnings.studentAssessments.allApproved
            ? ' (all approved)'
            : report.warnings.studentAssessments.allSubmitted
              ? ` (${report.warnings.studentAssessments.approved} approved)`
              : ''}
          {!report.warnings.studentAssessments.allSubmitted &&
          report.warnings.studentAssessments.pendingStudents?.length ? (
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              {report.warnings.studentAssessments.pendingStudents.map((s) => (
                <li key={s.studentId}>
                  {s.name} — {(s.status || 'not_started').replace(/_/g, ' ')}
                </li>
              ))}
            </Box>
          ) : null}
        </Alert>
      ) : null}
      {warnings.length > 0 && (
        <Alert severity="info">
          {warnings.map((w) => (
            <div key={w.code}>{w.message}</div>
          ))}
        </Alert>
      )}
      {report.rejectionReason ? (
        <Alert severity={report.status === 'rejected' ? 'warning' : 'info'}>
          Review note: {report.rejectionReason}
        </Alert>
      ) : null}
      <Paper variant="outlined" sx={{ p: 2, maxHeight: 420, overflow: 'auto' }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Session notes & plans
        </Typography>
        {(report.generatedSnapshot?.teaching?.batches || []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No class sessions in this month.
          </Typography>
        ) : (
          (report.generatedSnapshot?.teaching?.batches || []).map((batch) => (
            <MonthlyReportBatchTeaching key={batch.batchId} batch={batch} />
          ))
        )}
        {narrativeEntries.length > 0 && (
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
            <Typography variant="subtitle2">Teacher narrative</Typography>
            {narrativeEntries.map(([k, v]) => (
              <Box key={k} sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {k}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {v}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    </Stack>
  );
}

export default function SupervisorMonthlyReports({ embedded = false }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [month, setMonth] = useState(searchParams.get('month') || defaultMonth);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'submitted');
  const [teacherId, setTeacherId] = useState(searchParams.get('teacherId') || '');
  const [search, setSearch] = useState('');
  const [reviewTarget, setReviewTarget] = useState(null);
  const [viewTarget, setViewTarget] = useState(null);
  const [comment, setComment] = useState('');
  const queryClient = useQueryClient();

  const teachersQuery = useQuery(
    ['viewableTeachersForAchievementInbox'],
    async () => {
      const res = await teacherModuleAPI.getViewableTeachers();
      return res.data?.data?.teachers || [];
    },
    { staleTime: 5 * 60_000 }
  );

  const listQuery = useQuery(
    ['monthlyReportSubmissions', month, statusFilter, teacherId],
    async () => {
      const res = await teacherModuleAPI.listMonthlyReportSubmissions({
        month,
        status: statusFilter || undefined,
        teacherId: teacherId || undefined,
      });
      return res.data?.data || [];
    },
    { enabled: !!month }
  );

  const teacherOptions = useMemo(() => {
    const fromApi = (teachersQuery.data || []).map((t) => ({
      id: String(t.id),
      label: teacherOptionLabel(t),
    }));
    if (fromApi.length) return fromApi.sort((a, b) => a.label.localeCompare(b.label));
    const map = new Map();
    for (const row of listQuery.data || []) {
      if (!row.teacherId) continue;
      const id = String(row.teacherId);
      if (!map.has(id)) map.set(id, { id, label: row.teacherName || id });
    }
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [teachersQuery.data, listQuery.data]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return listQuery.data || [];
    return (listQuery.data || []).filter((row) =>
      String(row.teacherName || '').toLowerCase().includes(q)
    );
  }, [listQuery.data, search]);

  const reviewMutation = useMutation(
    ({ id, action, comment: c }) =>
      teacherModuleAPI.reviewMonthlyReport(id, { action, comment: c }),
    {
      onSuccess: () => {
        toast.success('Review saved');
        setReviewTarget(null);
        setComment('');
        queryClient.invalidateQueries(['monthlyReportSubmissions']);
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Failed to review'),
    }
  );

  const openReview = (row, action) => {
    setReviewTarget({ ...row, action });
    setComment('');
  };

  const syncParams = (next = {}) => {
    const nextTeacher = next.teacherId !== undefined ? next.teacherId : teacherId;
    const params = {
      month: next.month ?? month,
      status: next.status ?? statusFilter,
    };
    if (nextTeacher) params.teacherId = nextTeacher;
    setSearchParams(params);
  };

  return (
    <Box sx={embedded ? {} : { p: { xs: 2, md: 3 } }}>
      {!embedded && (
        <>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Monthly report inbox
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Review teacher achievement reports — approve or return with feedback.
          </Typography>
        </>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <TextField
          select
          size="small"
          label="Month"
          value={month}
          onChange={(e) => {
            setMonth(e.target.value);
            syncParams({ month: e.target.value });
          }}
          sx={{ minWidth: 140 }}
        >
          {Array.from({ length: 24 }).map((_, i) => {
            const m = shiftMonth('2026-06', i);
            if (m > defaultMonth) return null;
            return (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            );
          })}
        </TextField>
        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            syncParams({ status: e.target.value });
          }}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="submitted">Pending review</MenuItem>
          <MenuItem value="approved">Approved</MenuItem>
          <MenuItem value="rejected">Returned</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Teacher"
          value={teacherId}
          onChange={(e) => {
            setTeacherId(e.target.value);
            syncParams({ teacherId: e.target.value });
          }}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All teachers</MenuItem>
          {teacherOptions.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Search"
          placeholder="Teacher name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 200 }}
        />
      </Stack>

      {listQuery.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Teacher</TableCell>
                <TableCell>Month</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Student assessments</TableCell>
                <TableCell>Warnings</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.teacherName || '—'}</TableCell>
                  <TableCell>{row.reportMonth}</TableCell>
                  <TableCell>
                    <Chip size="small" label={row.status} />
                  </TableCell>
                  <TableCell>
                    {row.submittedAt ? new Date(row.submittedAt).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell>
                    <StudentAssessmentStatusCell summary={row.warnings?.studentAssessments} />
                  </TableCell>
                  <TableCell>{row.warnings?.incompleteSessionCount ?? 0} incomplete</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" onClick={() => setViewTarget(row)}>
                        View
                      </Button>
                      {row.status === 'submitted' && (
                        <>
                          <Button size="small" onClick={() => openReview(row, 'approved')}>
                            Approve
                          </Button>
                          <Button size="small" color="warning" onClick={() => openReview(row, 'rejected')}>
                            Reject
                          </Button>
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {!filteredRows.length && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No reports match these filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={!!viewTarget}
        onClose={() => setViewTarget(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Teacher achievement — {viewTarget?.teacherName} ({viewTarget?.reportMonth})
        </DialogTitle>
        <DialogContent>
          {viewTarget && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Chip size="small" label={viewTarget.status} sx={{ alignSelf: 'flex-start' }} />
              <TeacherAchievementReportBody report={viewTarget} />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewTarget(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!reviewTarget}
        onClose={() => setReviewTarget(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {reviewTarget?.action === 'approved' ? 'Approve report' : 'Return report for revision'}
        </DialogTitle>
        <DialogContent>
          {reviewTarget && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography>
                <strong>{reviewTarget.teacherName}</strong> — {reviewTarget.reportMonth}
              </Typography>
              <TeacherAchievementReportBody report={reviewTarget} />
              {reviewTarget.action === 'rejected' && (
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Feedback for teacher (min 10 chars)"
                  placeholder="Explain what the teacher should revise"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  error={
                    comment.trim().length > 0 &&
                    comment.trim().length < MIN_REJECT_COMMENT_LENGTH
                  }
                  helperText={rejectCommentHelperText(comment.trim().length)}
                  inputProps={{ maxLength: MAX_REJECT_COMMENT_LENGTH }}
                />
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={reviewTarget?.action === 'rejected' ? 'warning' : 'primary'}
            disabled={
              reviewMutation.isLoading ||
              (reviewTarget?.action === 'rejected' &&
                comment.trim().length < MIN_REJECT_COMMENT_LENGTH)
            }
            onClick={() =>
              reviewMutation.mutate({
                id: reviewTarget.id,
                action: reviewTarget.action,
                comment: comment.trim() || undefined,
              })
            }
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
